import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useAppSelector } from 'app/hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FaArrowUp, FaCalculator } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  purchaseAsset,
  resetAssetPurchaseStatus,
  selectAssetPurchaseStatus,
  selectError,
  selectLastAssetPurchase
} from '../../../slices/fundManagementInvestment/fundmanagementInvestmentSlice';
import { TbArrowsExchange } from 'react-icons/tb';
import { useDrawer } from 'components/app/DrawerContext';

interface GrowthAssetFormData {
  assetType: string;
  identifier: string;
  description: string;
  purchaseValue: number | string;
  expectedAnnualGrowth: string;
  quantity?: number | string;
  financingCosts: number | string;
  managementFees: number | string;
  operationalCosts: number | string;
  otherFees: number | string;
  incomeReinvestmentPolicy: string;
  hasFinancing: string;
  financingProvider?: string;
  loanAmount?: number | string;
  interestRate?: number | string;
  cashRequired: number | string;
  onetimeCharges: number | string;
  settlementDate: string;
  holdingPeriod?: number | string;
  exitStrategy?: string;
  developmentTimeline?: string;
  additionalNotes?: string;
  developmentPotential?: string;
  completionTarget?: string;
  growthDrivers?: string;
  exchange?: string;
  sector?: string;
  dividendPolicy?: string;
  lockupPeriod?: string;
  managementFee?: string;
  carriedInterest?: string;
}

interface PurchaseGrowthAssetsProps {
  onBack: () => void;
  cashRemaining: number;
}

const PurchaseGrowthAssets: React.FC<PurchaseGrowthAssetsProps> = ({ onBack, cashRemaining }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { fundManagementAdminRight } = useDrawer();

  const assetPurchaseStatus = useAppSelector(selectAssetPurchaseStatus);
  const lastAssetPurchase = useAppSelector(selectLastAssetPurchase);
  const error = useAppSelector(selectError);

  const selectedProjectId = useAppSelector(
    (state) => state.homeTilesServices.selectedProjectId || ''
  );
  const selectedServiceTitle = useAppSelector((state) => {
    const title = state.serviceSelected.selectedServiceTitle || 'fundManagementInvestment';
    return title.replace(/\s+/g, '').replace(/^[A-Z]/, (m) => m.toLowerCase());
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info'
  });

  const [fundManager] = useState(() => {
    const loginInfo = localStorage.getItem('user-details');
    if (loginInfo) {
      try {
        const userDetails = JSON.parse(loginInfo);
        return {
          name: userDetails.name || 'Fund Manager',
          email: userDetails.email || 'admin@fund.com'
        };
      } catch (error) {
        return { name: 'Fund Manager', email: 'admin@fund.com' };
      }
    }
    return { name: 'Fund Manager', email: 'admin@fund.com' };
  });

  const { control, handleSubmit, watch, setValue, reset } = useForm<GrowthAssetFormData>({
    defaultValues: {
      assetType: '',
      identifier: '',
      description: '',
      purchaseValue: '',
      expectedAnnualGrowth: '',
      quantity: '',
      financingCosts: '',
      managementFees: '',
      operationalCosts: '',
      otherFees: '',
      incomeReinvestmentPolicy: '',
      hasFinancing: 'false',
      cashRequired: '',
      onetimeCharges: '',
      settlementDate: new Date().toISOString().split('T')[0],
      holdingPeriod: '',
      exitStrategy: '',
      developmentTimeline: '',
      additionalNotes: '',
      developmentPotential: '',
      completionTarget: '',
      growthDrivers: '',
      exchange: '',
      sector: '',
      dividendPolicy: '',
      lockupPeriod: '',
      managementFee: '',
      carriedInterest: ''
    },
    mode: 'onChange'
  });

  const watchedValues = watch();

  const safeNumber = (value: any): number => {
    if (value === '' || value === null || value === undefined) return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const calculations = useMemo(() => {
    const totalMonthlyExpenses = safeNumber(
      (
        safeNumber(watchedValues.financingCosts) +
        safeNumber(watchedValues.managementFees) +
        safeNumber(watchedValues.operationalCosts) +
        safeNumber(watchedValues.otherFees)
      ).toFixed(2)
    );
    const annualExpenses = safeNumber((totalMonthlyExpenses * 12).toFixed(2));
    const purchaseVal = safeNumber(watchedValues.purchaseValue);
    const loanAmt = safeNumber(watchedValues.loanAmount);
    const loanToValue =
      loanAmt && purchaseVal > 0 ? safeNumber(((loanAmt / purchaseVal) * 100).toFixed(2)) : 0;

    let monthlyLoanPayment = 0;
    const interestRt = safeNumber(watchedValues.interestRate);
    if (loanAmt && interestRt) {
      const principal = loanAmt;
      const monthlyRate = interestRt / 100 / 12;
      const numPayments = 30 * 12;
      monthlyLoanPayment = safeNumber(
        (
          (principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments))) /
          (Math.pow(1 + monthlyRate, numPayments) - 1)
        ).toFixed(2)
      );
    }

    const projectedValue = (expectedGrowthRate: number, years: number): number => {
      if (expectedGrowthRate > 0 && years > 0) {
        return safeNumber((purchaseVal * Math.pow(1 + expectedGrowthRate / 100, years)).toFixed(2));
      }
      return 0;
    };

    const totalCostOfOwnership = (years: number): number => {
      return safeNumber((purchaseVal + annualExpenses * years).toFixed(2));
    };

    return {
      totalMonthlyExpenses,
      annualExpenses,
      loanToValue,
      monthlyLoanPayment,
      projectedValue,
      totalCostOfOwnership
    };
  }, [
    watchedValues.financingCosts,
    watchedValues.managementFees,
    watchedValues.operationalCosts,
    watchedValues.otherFees,
    watchedValues.purchaseValue,
    watchedValues.loanAmount,
    watchedValues.interestRate
  ]);

  useEffect(() => {
    if (watchedValues.hasFinancing === 'true' && calculations.monthlyLoanPayment > 0) {
      setValue('financingCosts', calculations.monthlyLoanPayment);
    }
  }, [calculations.monthlyLoanPayment, watchedValues.hasFinancing, setValue]);

  useEffect(() => {
    const cashNeeded =
      safeNumber(watchedValues.purchaseValue) -
      safeNumber(watchedValues.loanAmount) +
      safeNumber(watchedValues.onetimeCharges);
    if (cashNeeded > 0) {
      setValue('cashRequired', cashNeeded);
    }
  }, [
    watchedValues.purchaseValue,
    watchedValues.loanAmount,
    watchedValues.onetimeCharges,
    setValue
  ]);

  useEffect(() => {
    if (assetPurchaseStatus === 'succeeded' && lastAssetPurchase) {
      setSnackbar({
        open: true,
        message: `Growth Asset purchased successfully!`,
        severity: 'success'
      });

      setTimeout(() => {
        reset();
        dispatch(resetAssetPurchaseStatus());

        navigate('/fundManagementInvestment/request', {
          state: {
            from: 'purchaseAssets',
            to: 'growthUnits',
            isAdmin: fundManagementAdminRight
          }
        });
      }, 2000);
    }

    if (assetPurchaseStatus === 'failed' && error) {
      setSnackbar({
        open: true,
        message: error,
        severity: 'error'
      });
    }
  }, [
    assetPurchaseStatus,
    lastAssetPurchase,
    error,
    dispatch,
    reset,
    navigate,
    fundManagementAdminRight
  ]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getGrowthRate = () => {
    const growth = watchedValues.expectedAnnualGrowth;
    if (growth.includes('-')) {
      const [min] = growth.split('-');
      return parseFloat(min) || 0;
    }
    return parseFloat(growth.replace('%', '')) || 0;
  };

  const isFormValid = () => {
    return (
      watchedValues.assetType &&
      watchedValues.identifier &&
      watchedValues.description &&
      safeNumber(watchedValues.purchaseValue) > 0 &&
      watchedValues.expectedAnnualGrowth &&
      safeNumber(watchedValues.cashRequired) > 0 &&
      watchedValues.settlementDate
    );
  };

  const getVisibleFields = () => {
    const assetType = watchedValues.assetType.toLowerCase();

    if (assetType === 'other') {
      return {
        showPropertyFields: true,
        showStockFields: true,
        showAlternativeFields: true
      };
    }

    return {
      showPropertyFields:
        assetType.includes('property') ||
        assetType.includes('real estate') ||
        assetType.includes('development') ||
        assetType.includes('land'),
      showStockFields:
        assetType.includes('stock') ||
        assetType.includes('share') ||
        assetType.includes('equity') ||
        assetType.includes('growth stock'),
      showAlternativeFields:
        assetType.includes('crypto') ||
        assetType.includes('venture') ||
        assetType.includes('private equity') ||
        assetType.includes('fund')
    };
  };

  const visibleFields = getVisibleFields();

  const onSubmit = async (data: GrowthAssetFormData) => {
    if (safeNumber(data.cashRequired) > cashRemaining) {
      setSnackbar({
        open: true,
        message: `Cash required (${formatCurrency(
          safeNumber(data.cashRequired)
        )}) exceeds available cash (${formatCurrency(cashRemaining)})`,
        severity: 'error'
      });
      return;
    }

    if (!isFormValid()) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }

    const additionalInfo: any = {
      holdingPeriod: safeNumber(data.holdingPeriod).toString() || '5',
      exitStrategy: data.exitStrategy || '',
      developmentTimeline: data.developmentTimeline || '',
      additionalNotes: data.additionalNotes || '',
      developmentPotential: data.developmentPotential || '',
      completionTarget: data.completionTarget || '',
      growthDrivers: data.growthDrivers || '',
      exchange: data.exchange || '',
      sector: data.sector || '',
      dividendPolicy: data.dividendPolicy || '',
      lockupPeriod: data.lockupPeriod || '',
      managementFee: data.managementFee || '',
      carriedInterest: data.carriedInterest || ''
    };

    const qty = safeNumber(data.quantity);
    if (qty) {
      additionalInfo.quantity = qty.toString();
    }

    const oneTimeChg = safeNumber(data.onetimeCharges);
    if (oneTimeChg) {
      additionalInfo.onetimeCharges = oneTimeChg.toString();
    }

    const loanAmt = safeNumber(data.loanAmount);
    const liabilities =
      data.hasFinancing === 'true' && loanAmt
        ? [
            {
              type: 'financing',
              provider: data.financingProvider || '',
              amount: loanAmt.toString(),
              rate: `${safeNumber(data.interestRate)}%`,
              monthlyPayment: calculations.monthlyLoanPayment.toString()
            }
          ]
        : [];

    const payload = {
      groupId: selectedProjectId,
      serviceId: selectedServiceTitle,
      unitType: 'growthUnits' as const,
      asset: {
        type: data.assetType,
        subType: 'custom',
        identifier: data.identifier,
        description: data.description,
        purchaseValue: safeNumber(data.purchaseValue).toString(),
        cashUsed: safeNumber(data.cashRequired).toString(),
        expectedAnnualYield: data.expectedAnnualGrowth,
        monthlyIncome: {
          primary: '0',
          secondary: '0',
          other: '0',
          totalGrossIncome: '0'
        },
        monthlyExpenses: {
          financing: safeNumber(data.financingCosts).toString(),
          management: safeNumber(data.managementFees).toString(),
          operational: safeNumber(data.operationalCosts).toString(),
          fees: safeNumber(data.otherFees).toString(),
          totalExpenses: calculations.totalMonthlyExpenses.toString()
        },
        netMonthlyIncome: '0',
        liabilities,
        additionalInfo
      }
    };

    dispatch(purchaseAsset(payload));
  };

  const loading = assetPurchaseStatus === 'pending';

  return (
    <Container maxWidth="xl" sx={{ padding: { xs: 1, sm: 2, md: 3 }, minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: { xs: 2, md: 3 } }}>
        <FaArrowUp size={isMobile ? 20 : 24} color="#1976d2" style={{ marginRight: 12 }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#333' }}>
            Purchase Growth Assets
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Fund Manager: {fundManager.name} • Available Cash: {formatCurrency(cashRemaining)}
          </Typography>
        </Box>
      </Box>

      {safeNumber(watchedValues.cashRequired) > cashRemaining && (
        <Alert severity="error" sx={{ marginBottom: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            ⚠️ Insufficient Cash Available
          </Typography>
          <Typography variant="body2">
            Cash required: <strong>{formatCurrency(safeNumber(watchedValues.cashRequired))}</strong>{' '}
            exceeds available cash: <strong>{formatCurrency(cashRemaining)}</strong>
          </Typography>
          <Typography variant="body2" sx={{ marginTop: 1, fontSize: '0.875rem' }}>
            Please reduce the purchase value or increase financing to proceed with this asset
            purchase.
          </Typography>
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Button
              onClick={onBack}
              variant="outlined"
              fullWidth
              startIcon={<TbArrowsExchange />}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: 2,
                paddingY: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.03)'
              }}
            >
              Change Class
            </Button>
            <Paper sx={{ padding: 3, borderRadius: 3, position: 'sticky', top: 150, marginTop: 2 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 500, marginBottom: 2, display: 'flex', alignItems: 'center' }}
              >
                <FaCalculator size={16} color="#1976d2" style={{ marginRight: 8 }} />
                Growth Analysis
              </Typography>

              <Box
                sx={{ marginBottom: 2, padding: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#1976d2' }}>
                  Growth Asset Acquisition
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {watchedValues.assetType || 'Select Asset Type'}
                </Typography>
              </Box>

              <Divider sx={{ marginY: 2 }} />

              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Purchase Value:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatCurrency(safeNumber(watchedValues.purchaseValue))}
                  </Typography>
                </Box>

                {safeNumber(watchedValues.onetimeCharges) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      One-time Charges:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#ff9800' }}>
                      {formatCurrency(safeNumber(watchedValues.onetimeCharges))}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Cash Required:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color:
                        safeNumber(watchedValues.cashRequired) > cashRemaining
                          ? '#f44336'
                          : 'inherit'
                    }}
                  >
                    {formatCurrency(safeNumber(watchedValues.cashRequired))}
                  </Typography>
                </Box>

                {safeNumber(watchedValues.cashRequired) > cashRemaining && (
                  <Box
                    sx={{
                      marginTop: 1,
                      marginBottom: 1,
                      padding: 1.5,
                      backgroundColor: '#ffebee',
                      borderRadius: 1,
                      border: '1px solid #f44336'
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: '#d32f2f', fontWeight: 500, display: 'block' }}
                    >
                      ⚠️ Exceeds Available Cash
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block' }}>
                      Shortfall:{' '}
                      {formatCurrency(safeNumber(watchedValues.cashRequired) - cashRemaining)}
                    </Typography>
                  </Box>
                )}

                {watchedValues.hasFinancing === 'true' &&
                  safeNumber(watchedValues.loanAmount) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        Loan Amount:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(safeNumber(watchedValues.loanAmount))}
                      </Typography>
                    </Box>
                  )}

                <Divider sx={{ marginY: 1 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Monthly Expenses:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#f44336' }}>
                    -{formatCurrency(calculations.totalMonthlyExpenses)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Annual Expenses:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#f44336' }}>
                    -{formatCurrency(calculations.annualExpenses)}
                  </Typography>
                </Box>

                <Divider sx={{ marginY: 1 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Expected Growth:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#4caf50' }}>
                    {watchedValues.expectedAnnualGrowth || 'TBD'}
                  </Typography>
                </Box>

                {getGrowthRate() > 0 && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        5-Year Projection:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        {formatCurrency(calculations.projectedValue(getGrowthRate(), 5))}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Total Cost (5Y):
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff9800' }}>
                        {formatCurrency(calculations.totalCostOfOwnership(5))}
                      </Typography>
                    </Box>
                  </>
                )}

                {calculations.loanToValue > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      LVR:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff9800' }}>
                      {calculations.loanToValue}%
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper sx={{ padding: 4, borderRadius: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 500, marginBottom: 2 }}>
                Asset Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="assetType"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth required>
                        <InputLabel>Asset Type *</InputLabel>
                        <Select
                          {...field}
                          label="Asset Type *"
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <MenuItem value="Development Property">Development Property</MenuItem>
                          <MenuItem value="Growth Stock">Growth Stock</MenuItem>
                          <MenuItem value="Technology Stock">Technology Stock</MenuItem>
                          <MenuItem value="Growth Real Estate">Growth Real Estate</MenuItem>
                          <MenuItem value="Private Equity">Private Equity</MenuItem>
                          <MenuItem value="Venture Capital">Venture Capital</MenuItem>
                          <MenuItem value="Cryptocurrency">Cryptocurrency</MenuItem>
                          <MenuItem value="Commodities">Commodities</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Controller
                    name="purchaseValue"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Purchase Value *"
                        type="number"
                        required
                        InputProps={{
                          startAdornment: <InputAdornment position="start">AUD</InputAdornment>
                        }}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === '' ? '' : parseFloat(e.target.value) || ''
                          )
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Controller
                    name="expectedAnnualGrowth"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Expected Growth *"
                        required
                        placeholder="e.g., 10-15% or 12%"
                        helperText="Annual growth rate or range"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="identifier"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Asset Identifier *"
                        required
                        helperText="e.g., Development site address, Stock ticker, Fund name, etc."
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Description *"
                        multiline
                        rows={2}
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="quantity"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Quantity"
                        type="number"
                        helperText="Number of units/shares (if applicable)"
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === '' ? '' : parseFloat(e.target.value) || ''
                          )
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="holdingPeriod"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Expected Holding Period"
                        type="number"
                        helperText="Years (e.g., 5, 10)"
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === '' ? '' : parseFloat(e.target.value) || ''
                          )
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ marginY: 3 }} />
              <Typography variant="body1" sx={{ fontWeight: 500, marginBottom: 2 }}>
                Monthly Expenses
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Controller
                    name="financingCosts"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Financing Costs"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === '' ? '' : parseFloat(e.target.value) || ''
                          )
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Controller
                    name="managementFees"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Management Fees"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === '' ? '' : parseFloat(e.target.value) || ''
                          )
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Controller
                    name="operationalCosts"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Operational Costs"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === '' ? '' : parseFloat(e.target.value) || ''
                          )
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Controller
                    name="otherFees"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Other Fees"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === '' ? '' : parseFloat(e.target.value) || ''
                          )
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {visibleFields.showPropertyFields && (
                <>
                  <Divider sx={{ marginY: 3 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500, marginBottom: 2 }}>
                    Development/Property Details
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="developmentPotential"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Development Potential"
                            placeholder="Residential, Commercial"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="completionTarget"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Completion Target"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="developmentTimeline"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Development Timeline"
                            placeholder="e.g., 2-3 years"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Controller
                        name="growthDrivers"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Growth Drivers"
                            multiline
                            rows={2}
                            helperText="Key factors driving growth (e.g., infrastructure, demand)"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {visibleFields.showStockFields && (
                <>
                  <Divider sx={{ marginY: 3 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500, marginBottom: 2 }}>
                    Stock/Equity Details
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="exchange"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Exchange"
                            placeholder="ASX, NYSE, NASDAQ"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="sector"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Sector"
                            placeholder="Technology, Healthcare"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="dividendPolicy"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Dividend Policy"
                            placeholder="Reinvestment, Payout"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {visibleFields.showAlternativeFields && (
                <>
                  <Divider sx={{ marginY: 3 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500, marginBottom: 2 }}>
                    Alternative Investment Details
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="lockupPeriod"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Lock-up Period"
                            placeholder="e.g., 5 years"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="managementFee"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Management Fee %"
                            placeholder="e.g., 2%"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="carriedInterest"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Carried Interest %"
                            placeholder="e.g., 20%"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              <Divider sx={{ marginY: 3 }} />
              <Typography variant="body1" sx={{ fontWeight: 500, marginBottom: 2 }}>
                Financing Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="hasFinancing"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 500 }}>
                          Financing Required *
                        </Typography>
                        <RadioGroup {...field} row onChange={(e) => field.onChange(e.target.value)}>
                          <FormControlLabel
                            value="false"
                            control={<Radio />}
                            label="No - Cash Purchase"
                          />
                          <FormControlLabel
                            value="true"
                            control={<Radio />}
                            label="Yes - Loan Required"
                          />
                        </RadioGroup>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="financingProvider"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Financing Provider"
                        helperText="Bank or lender name"
                        disabled={watchedValues.hasFinancing !== 'true'}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Controller
                    name="loanAmount"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Loan Amount"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === '' ? '' : parseFloat(e.target.value) || ''
                          )
                        }
                        disabled={watchedValues.hasFinancing !== 'true'}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Controller
                    name="interestRate"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Interest Rate"
                        type="number"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">% p.a.</InputAdornment>
                        }}
                        inputProps={{ min: 0, max: 20, step: 0.01 }}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === '' ? '' : parseFloat(e.target.value) || ''
                          )
                        }
                        disabled={watchedValues.hasFinancing !== 'true'}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ marginY: 3 }} />
              <Typography variant="body1" sx={{ fontWeight: 500, marginBottom: 2 }}>
                Settlement Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="onetimeCharges"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="One-time Charges"
                        type="number"
                        helperText="Stamp duty, legal fees, etc."
                        InputProps={{
                          startAdornment: <InputAdornment position="start">AUD</InputAdornment>
                        }}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === '' ? '' : parseFloat(e.target.value) || ''
                          )
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Controller
                    name="cashRequired"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Cash Required *"
                        type="number"
                        required
                        helperText="Auto-calculated"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">AUD</InputAdornment>,
                          readOnly: true
                        }}
                        sx={{ backgroundColor: '#f5f5f5' }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Controller
                    name="settlementDate"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Settlement Date *"
                        type="date"
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ marginY: 3 }} />
              <Controller
                name="additionalNotes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Additional Notes"
                    multiline
                    rows={3}
                    helperText="Exit strategy, market outlook, risk factors, etc."
                  />
                )}
              />

              <Box sx={{ marginTop: 4, textAlign: 'center' }}>
                <Alert severity="info" sx={{ marginBottom: 3 }}>
                  This growth asset will be added to the fund portfolio for capital appreciation and
                  long-term fund value growth.
                </Alert>

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={
                    loading ||
                    !isFormValid() ||
                    safeNumber(watchedValues.cashRequired) > cashRemaining
                  }
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                  sx={{
                    backgroundColor: '#1976d2',
                    '&:hover': { backgroundColor: '#1565c0' },
                    textTransform: 'none',
                    fontWeight: 500,
                    padding: '12px 36px'
                  }}
                >
                  {loading ? 'Processing...' : 'Purchase Growth Asset'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PurchaseGrowthAssets;
